import type { FragmentType } from "../../../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../../../__generated__/gql";
import { DxButton } from "../../../../_components/dx-button";
import { DxMainVisual } from "../../../../_components/dx-main-visual";
import { DxPageHeader } from "../../../../_components/dx-page-header";
import { buildSiteHeroImages } from "../_utils/build-site-hero-images";

export const SiteHeroFragment = graphql(`
  fragment SiteHero_Site on Site {
    name
    content {
      prefecture
      city
      mapImageUrl
      heroImages {
        url
        caption
      }
    }
    accommodations {
      id
      architectureType {
        id
        name
      }
    }
  }
`);

export function SiteHero({
  site: siteData,
  areaSlug,
  showAreaLink = true,
}: {
  site: FragmentType<typeof SiteHeroFragment>;
  areaSlug?: string;
  /** 拠点詳細モーダル表示時は sidebar 相当の導線が無いため、エリア情報リンクごと非表示にする。 */
  showAreaLink?: boolean;
}) {
  const site = useFragment(SiteHeroFragment, siteData);
  // 拠点案内図 (mapImageUrl) をヒーローの 2 枚目に差し込む (PIL-437)。
  const heroImages = buildSiteHeroImages(
    site.content.heroImages,
    site.content.mapImageUrl,
  );
  const locationLabel = [site.content.prefecture, site.content.city]
    .filter(Boolean)
    .join("");
  const architectureTypes = Array.from(
    new Set(site.accommodations.map((a) => a.architectureType.name)),
  ).sort((a, b) => a.localeCompare(b, "ja"));
  // サブタイトル = 所在地 + 建築タイプ (仕様 ③-1 / ③-2)。
  const subtitleParts = [locationLabel, architectureTypes.join("・")].filter(
    Boolean,
  );
  const subtitle =
    subtitleParts.length > 0 ? subtitleParts.join(" ｜ ") : undefined;
  return (
    // section に横 padding を置かず PageHeader と MainVisual が各自 px-16 を持つ (pane の 16px と合算)。
    // 画像は <lg full-bleed、lg+ は lg:px-16 で PageHeader と左端を揃える。
    <section>
      <DxPageHeader
        title={site.name}
        subtitle={subtitle}
        actions={
          // エリア情報導線。 lg+ は sidebar が担うため <lg のみ表示。
          showAreaLink ? (
            <DxButton
              variant="ghost"
              href={`/areas/${areaSlug}`}
              className="lg:hidden"
              data-analytics-id="site_detail_clicked_area_info"
            >
              エリア情報
            </DxButton>
          ) : undefined
        }
      />
      <div className="xs:px-[16px]">
        <DxMainVisual
          images={heroImages}
          altText={`${site.name} のメインビジュアル`}
        />
      </div>
    </section>
  );
}
